import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { baseAPI } from '../../../config';
import { XMarkIcon, MagnifyingGlassIcon, FolderIcon } from '@heroicons/react/24/outline';
import debounce from 'lodash/debounce';

const AssignEmailModal = ({ email, onClose, onAssigned }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expedients, setExpedients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedExpedient, setSelectedExpedient] = useState(null);
  const [assigning, setAssigning] = useState(false);

  // Debounced search function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchExpedients = useCallback(
    debounce(async term => {
      if (!term || term.length < 2) {
        setExpedients([]);
        return;
      }

      setLoading(true);
      try {
        const response = await axios.get(`${baseAPI}/expedients`, {
          params: { search: term, limit: 10 },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setExpedients(response.data?.expedients || []);
      } catch (error) {
        console.error('Error searching expedients:', error);
        setExpedients([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    searchExpedients(searchTerm);
  }, [searchTerm, searchExpedients]);

  const handleAssign = async () => {
    if (!selectedExpedient || !email) return;

    setAssigning(true);
    try {
      await axios.post(
        `${baseAPI}/emails/unmatched/${email._id}/assign`,
        { expedient_id: selectedExpedient._id },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      onAssigned(email._id);
    } catch (error) {
      console.error('Error assigning email:', error);
      toast.error(error.response?.data?.message || 'Error al asignar el email');
    } finally {
      setAssigning(false);
    }
  };

  if (!email) return null;

  return (
    <div className='fixed inset-0 z-50 overflow-y-auto'>
      <div className='flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0'>
        {/* Backdrop */}
        <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' onClick={onClose} />

        {/* Modal panel */}
        <div className='inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full'>
          {/* Header */}
          <div className='flex items-center justify-between px-6 py-4 border-b border-gray-200'>
            <h3 className='text-lg font-semibold text-gray-800'>Asignar email a expediente</h3>
            <button onClick={onClose} className='text-gray-400 hover:text-gray-600'>
              <XMarkIcon className='w-5 h-5' />
            </button>
          </div>

          {/* Email info */}
          <div className='px-6 py-3 bg-gray-50 border-b border-gray-200'>
            <p className='text-sm text-gray-600'>
              <span className='font-medium'>De:</span> {email.from_name || email.from_email}
            </p>
            <p className='text-sm text-gray-600 truncate'>
              <span className='font-medium'>Asunto:</span> {email.subject || '(Sin asunto)'}
            </p>
          </div>

          {/* Search */}
          <div className='px-6 py-4'>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              Buscar expediente
            </label>
            <div className='relative'>
              <MagnifyingGlassIcon className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' />
              <input
                type='text'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder='Buscar por deudor, cliente, NIF...'
                className='w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500'
                autoFocus
              />
            </div>
          </div>

          {/* Results */}
          <div className='px-6 pb-4 max-h-64 overflow-y-auto'>
            {loading ? (
              <div className='text-center py-4'>
                <div className='inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500'></div>
              </div>
            ) : expedients.length === 0 ? (
              searchTerm.length >= 2 ? (
                <p className='text-sm text-gray-500 text-center py-4'>
                  No se encontraron expedientes
                </p>
              ) : (
                <p className='text-sm text-gray-500 text-center py-4'>
                  Escribe al menos 2 caracteres para buscar
                </p>
              )
            ) : (
              <div className='space-y-2'>
                {expedients.map(expedient => (
                  <button
                    key={expedient._id}
                    type='button'
                    onClick={() => setSelectedExpedient(expedient)}
                    className={`w-full p-3 text-left rounded-lg border transition-colors ${
                      selectedExpedient?._id === expedient._id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className='flex items-start gap-3'>
                      <FolderIcon className='w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0' />
                      <div className='min-w-0'>
                        <p className='font-medium text-gray-800 truncate'>
                          {expedient.deudores_id?.[0]?.debtorname || 'Sin deudor'}
                        </p>
                        <p className='text-sm text-gray-500 truncate'>
                          {expedient.deudores_id?.[0]?.debtormail || expedient.deudores_id?.[0]?.debtornif || ''}
                        </p>
                        <p className='text-xs text-gray-400 mt-1'>
                          {expedient.user_id?.username || 'Sin cliente'} - {expedient.importe_deuda}€
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className='px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3'>
            <button
              type='button'
              onClick={onClose}
              className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50'
            >
              Cancelar
            </button>
            <button
              type='button'
              onClick={handleAssign}
              disabled={!selectedExpedient || assigning}
              className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${
                !selectedExpedient || assigning
                  ? 'bg-blue-300 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {assigning ? 'Asignando...' : 'Asignar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignEmailModal;
